
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useDebounce } from "@/hooks/use-debounce";
import { useToast } from "@/hooks/use-toast";
import { AdminBill } from "../types";

// Define an interface for the bill data returned from Supabase
interface BillData {
  id: string;
  amount: number;
  tax: number;
  discount: number;
  status: string;
  due_date: string;
  description: string | null;
  transaction_number: string;
  discount_type: string | null;
  plan_id: string;
  plan_name: string;
  plan_price: number;  // Updated to expect plan_price as a number
  product_name: string;
  school_name: string;
}

export const useBillsData = (initialStatusFilter: string | null = null) => {
  const [bills, setBills] = useState<AdminBill[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(initialStatusFilter);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { toast } = useToast();

  // Effect to update status filter when initialStatusFilter changes
  useEffect(() => {
    if (initialStatusFilter) {
      setStatusFilter(initialStatusFilter);
    }
  }, [initialStatusFilter]);

  const fetchBills = async () => {
    try {
      setLoading(true);
      
      // Log the parameters being sent to the database
      console.log("Fetching bills with params:", {
        search: debouncedSearchTerm,
        page: currentPage,
        pageSize: pageSize,
        status: statusFilter
      });
      
      const { data, error } = await supabase
        .rpc('get_filtered_bills', {
          search_query: debouncedSearchTerm,
          page_number: currentPage,
          page_size: pageSize,
          status_filter: statusFilter
        });

      if (error) throw error;

      if (data?.[0]) {
        const { total_count, bills: billsData } = data[0];
        setTotalCount(total_count);
        
        // Convert to unknown first, then to BillData[] to satisfy TypeScript
        const billsArray = Array.isArray(billsData) 
          ? (billsData as unknown as BillData[]) 
          : [];
        
        // Debug the raw bills data
        console.log("Raw bills data:", billsArray);
        
        const transformedBills = billsArray.map((bill: BillData) => {
          // Debug individual bill data
          console.log(`Processing bill ${bill.id}:`, {
            plan_name: bill.plan_name,
            plan_price: bill.plan_price,
            product_name: bill.product_name
          });
          
          return {
            id: bill.id,
            amount: bill.amount,
            tax: bill.tax,
            discount: bill.discount,
            status: bill.status,
            due_date: bill.due_date,
            description: bill.description,
            transaction_number: bill.transaction_number,
            discount_type: bill.discount_type,
            plan_price: bill.plan_price, // Include plan_price in the transformed bill
            plan: {
              name: bill.plan_name,
              price: bill.plan_price,
              product: {
                name: bill.product_name
              }
            },
            client: {
              nama_sekolah: bill.school_name
            }
          };
        });

        // Debug the transformed bills
        console.log("Transformed bills:", transformedBills);
        
        setBills(transformedBills);
      }
    } catch (error) {
      console.error('Error fetching bills:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch bills"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchBills();
  }, [debouncedSearchTerm, statusFilter, pageSize]);

  useEffect(() => {
    fetchBills();
  }, [currentPage]);

  return {
    bills,
    loading,
    updating,
    setUpdating,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    totalCount,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    fetchBills
  };
};
